// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CustomEvent } from '@shared/utils/CustomEvents';
import { ControlsUtils } from '../../UtilsClasses/ControlsUtils';
import TerceraQuickTreeTemplate from '@front/templates/TerceraQuickTreeTemplate.html';
import { Control } from '../Control';
import { QuickTree } from './QuickTree';
import { KeyEventProcessor } from '@shared/commons/KeyEventProcessor';
export class TerceraQuickTree extends Control {
    constructor() {
        super();
        this.quickTree = null;
    }
    getType() {
        return 'TerceraQuickTree';
    }
    oninit() {
        TerceraQuickTree.uniqueId++;
        void this.set({ myId: TerceraQuickTree.uniqueId });
        const keyProc = KeyEventProcessor;
        keyProc.OnKeyDown.Subscribe(this.onGlobalKeyDown, this);
        keyProc.OnKeyUp.Subscribe(this.onGlobalKeyUp, this);
        this.GlobalKeyDownHandled = new CustomEvent();
    }
    setSizes() {
        const panel = this.find('*');
        const w = panel.offsetWidth;
        const h = panel.offsetHeight;
        void this.set({ width: w, height: h });
    }
    resetSizes() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.set({ width: 0, height: 0 });
        });
    }
    onrender() {
        const canvas = document.getElementById('tree_' + TerceraQuickTree.uniqueId);
        const ctx = canvas.getContext('2d');
        this.quickTree = new QuickTree(ctx);
        this.quickTree.showTooltip = (text, rect) => {
            showTooltipOnCanvas(text, canvas, rect);
        };
        this.quickTree.boldRootNode = this.get('boldRootNode');
        this.quickTree.isMultiSelect = false;
        this.quickTree.SelectionChanged.Subscribe(this.onSelectionChanged, this);
        this.quickTree.EnterPressed.Subscribe(this.onEnterPressed, this);
        this.observe('width', (newValue, oldValue, keypath) => {
            if (isValidNumber(newValue)) {
                const devicePixelRatio = window.devicePixelRatio || 1;
                this.quickTree.width = newValue;
                canvas.width = this.quickTree.width * devicePixelRatio;
                ctx.scale(devicePixelRatio, devicePixelRatio);
            }
            else {
                this.quickTree.width = newValue;
            }
            this.quickTree.onResize();
        }, { defer: true });
        this.observe('height', (newValue, oldValue, keypath) => {
            if (isValidNumber(newValue)) {
                const devicePixelRatio = window.devicePixelRatio || 1;
                this.quickTree.height = newValue;
                canvas.height = this.quickTree.height * devicePixelRatio;
                ctx.scale(devicePixelRatio, devicePixelRatio);
            }
            else {
                this.quickTree.height = newValue;
            }
            this.quickTree.onResize();
        }, { defer: true });
    }
    onEnterPressed() {
        this.fire(TerceraQuickTreeEvents.EnterPress);
    }
    onSelectionChanged() {
        this.fire(TerceraQuickTreeEvents.Selection);
    }
    onMouseDown(event) {
        super.onMouseDown(event);
        this.quickTree.onMouseDown(event.original);
        ControlsUtils.Capture.SetCapture(this, event.original);
        this.fire(TerceraQuickTreeEvents.AfterMouseDown, event);
    }
    onMouseMove(event) {
        if (this.captured && event.original) {
            return;
        }
        super.onMouseMove(event);
        this.quickTree.onMouseMove(event.original || event);
        void this.set('currentCursor', this.quickTree.currentCursor);
    }
    onMouseUp(event) {
        if (this.captured && event.original) {
            return;
        }
        super.onMouseUp(event);
        this.quickTree.onMouseUp(event.original || event);
    }
    onMouseWheel(context) {
        const event = context.event;
        super.onMouseWheel(event);
        this.quickTree.onMouseWheel(event);
    }
    onDoubleClick(event) {
        // if (this.quickTree.isMultiSelect)   //  https://tp.traderevolution.com/entity/84756  (2nd issue)
        //  return;
        super.onDoubleClick(event);
        const res = this.quickTree.onDoubleClick(event.original);
        if (res) {
            this.fire(TerceraQuickTreeEvents.DoubleClick, this);
        }
    }
    onMouseLeave(context) {
        const event = context.event;
        super.onMouseLeave(event);
        this.quickTree.onMouseLeave(event);
    }
    onGlobalKeyDown() {
        const container = this.get('parentContainerControl');
        if ((container === null || container === void 0 ? void 0 : container.get('focused')) || this.get('focused')) {
            this.quickTree.onKeyDown();
            this.GlobalKeyDownHandled.Raise();
        }
    }
    onGlobalKeyUp() {
        const container = this.get('parentContainerControl');
        if ((container === null || container === void 0 ? void 0 : container.get('focused')) || this.get('focused')) {
            this.quickTree.onKeyUp();
        }
    }
    onteardown() {
        const keyProc = KeyEventProcessor;
        keyProc.OnKeyDown.UnSubscribe(this.onGlobalKeyDown, this);
        keyProc.OnKeyUp.UnSubscribe(this.onGlobalKeyUp, this);
    }
    setIsMultiSelect(isMultiSelect) {
        this.quickTree.isMultiSelect = isMultiSelect;
    }
}
TerceraQuickTree.uniqueId = 0;
Control.extendWith(TerceraQuickTree, {
    template: TerceraQuickTreeTemplate,
    data: function () {
        return {
            myId: 0,
            boldRootNode: false,
            currentCursor: ''
        };
    }
});
export var TerceraQuickTreeEvents;
(function (TerceraQuickTreeEvents) {
    TerceraQuickTreeEvents["EnterPress"] = "EnterPress";
    TerceraQuickTreeEvents["DoubleClick"] = "DoubleClick";
    TerceraQuickTreeEvents["Selection"] = "selection";
    TerceraQuickTreeEvents["AfterMouseDown"] = "afterMouseDown";
})(TerceraQuickTreeEvents || (TerceraQuickTreeEvents = {}));
